/* eslint-disable react-refresh/only-export-components -- TODO: split Table V2 helper/style exports from component exports once the broad table barrel is retired. */
import { type ReactElement, type ReactNode, useMemo } from 'react';
import { buildTagColorByName, mergeTagColorByName, resolveTagColor } from '@/features/shared/tags';
import { useTagOptions } from '@/shared/hooks';
import { isLightTheme } from '@/theme/semanticTokens';
import { iconSizes, r, s, typographyTokens } from '@/theme/tokens';
import { lightModeStatusColors, uiTokens } from '@/theme/uiTokens';
import { OverflowTooltip } from '@/ui/composites/table/cells/OverflowTooltip';
import { TableTagChipWithTooltip } from '@/ui/composites/table/cells/TableTagChipWithTooltip';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Text,
  Tooltip,
  resolveStatusChipLabel,
  resolveStatusChipTone,
} from '@/ui/primitives';
import { alpha } from '@/ui/primitives/colorUtils';
import { KeyboardArrowDownIcon, MoreVertIcon, SettingsIcon } from '@/ui/primitives/icons';
import { tableControlHoverBackground, tableControlSurfaceBackground } from './tablePanelSurfaceSx';
import { tableV2BodyRowHeightPx, tableV2HeaderHeight } from './TableV2Constants';
import { tableV2HeaderLabelSx } from './TableV2HeaderStyles';
import {
  tableV2DenseTagChipSx,
  tableV2DenseTagChipWrapperSx,
  tableV2DenseTagGroupSx,
  tableV2TagChipSx,
  tableV2TagGroupSx,
  tableV2TooltipTagChipSx,
} from './TableV2TagGroupStyles';
import type { TagColor } from '@/shared/data/tags';
import type { UiTone } from '@/theme/uiTokens';
import type { ApiSort } from '@/ui/composites/grid/gridUtils';
import type { SxProps, Theme } from '@/ui/primitives';
import type { StatusChipStatus } from '@/ui/primitives/statusChips';

export { TableV2SkeletonRow } from './TableV2Skeleton';
export type { TableV2SkeletonColumnKind } from './TableV2Skeleton';
export { TableV2ChevronButton } from './TableV2Chevron';
export { tableV2ChevronCellSx } from './TableV2ChevronCellStyles';
export { TableV2NameCell } from './TableV2NameCell';
export { formatTableV2PassRate } from './TableV2PassRateCell.helpers';
export type { TableV2Counts } from './TableV2PassRateCell.helpers';
export { TableV2PassRateCell, TableV2PassRateStatusBar } from './TableV2PassRateCell';
export { TableV2ProjectCell } from './TableV2ProjectCell';

export {
  tableV2ActionsColumnWidth,
  tableV2BodyRowHeightPx,
  tableV2ChevronColumnWidth,
  tableV2FooterControlHeightPx,
  tableV2FooterHeight,
  tableV2HeaderHeight,
  tableV2MinimumVisibleRows,
  tableV2NamePrimaryFontSize,
  tableV2NestedBodyRowHeightPx,
  tableV2NestedHeaderHeight,
} from './TableV2Constants';

export const tableV2MinimumTableWidth = '1080px';

export const tableV2RootSx = {
  backgroundColor: tableControlSurfaceBackground,
  borderCollapse: 'separate',
  borderSpacing: 0,
  tableLayout: 'fixed',
  minWidth: tableV2MinimumTableWidth,
  '& th': {
    backgroundColor: tableControlSurfaceBackground,
    borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
    color: (theme) => theme.app.colors.textMuted,
    fontSize: '13px',
    fontWeight: 500,
    height: tableV2HeaderHeight,
    lineHeight: 1.3,
    overflow: 'hidden',
    position: 'sticky',
    px: s('lg'),
    py: s('none'),
    textOverflow: 'ellipsis',
    top: 0,
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    zIndex: 2,
  },
  '&& > tbody > tr': {
    height: `${String(tableV2BodyRowHeightPx)}px`,
    maxHeight: `${String(tableV2BodyRowHeightPx)}px`,
  },
  '& > tbody > tr > td': {
    boxSizing: 'border-box',
    fontSize: '13px',
    height: `${String(tableV2BodyRowHeightPx)}px`,
    maxHeight: `${String(tableV2BodyRowHeightPx)}px`,
    overflow: 'hidden',
    px: s('lg'),
    py: '0 !important',
    verticalAlign: 'middle',
  },
  '& .MuiTypography-root': {
    fontSize: '13px',
  },
} satisfies SxProps<Theme>;

export const tableV2PanelSx = {
  overflow: 'hidden',
  overflowX: 'auto',
  scrollbarColor: (theme) => `${theme.app.colors.border} transparent`,
  scrollbarWidth: 'thin',
  width: '100%',
  '&::-webkit-scrollbar': {
    backgroundColor: 'transparent',
    height: s('sm'),
    width: s('sm'),
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
    borderRadius: r('pill'),
  },
  '&::-webkit-scrollbar-corner': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundClip: 'content-box',
    backgroundColor: (theme) => theme.app.colors.border,
    border: `${s('xxs')} solid transparent`,
    borderRadius: r('pill'),
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: (theme) => theme.app.colors.textMuted,
  },
} satisfies SxProps<Theme>;

export const tableV2FrameSx = {
  overflow: 'hidden',
  width: '100%',
} satisfies SxProps<Theme>;

export const TableV2NestedCollapse = ({
  children,
  expanded,
}: {
  children: ReactNode;
  expanded: boolean;
}): ReactElement | null => {
  return (
    <Collapse
      aria-hidden={!expanded}
      in={expanded}
      timeout={220}
      mountOnEnter
      unmountOnExit
      sx={{
        '& .MuiCollapse-wrapperInner': {
          overflow: 'hidden',
        },
        width: '100%',
      }}
    >
      <Box
        sx={{
          backgroundColor: 'transparent',
          minHeight: 0,
          overflow: 'hidden',
          transform: expanded ? 'translateY(0)' : 'translateY(-8px)',
          transition: (theme) =>
            theme.transitions.create(['opacity', 'transform'], {
              duration: expanded ? 180 : 220,
              easing: theme.transitions.easing.easeInOut,
            }),
          width: '100%',
        }}
      >
        {children}
      </Box>
    </Collapse>
  );
};

export type TableV2PaginationToken = number | 'ellipsis';

const MAX_TABLE_V2_PAGINATION_TOKENS = 7;

export const buildTableV2PaginationTokens = (
  page: number,
  pageCount: number,
): TableV2PaginationToken[] => {
  if (pageCount <= MAX_TABLE_V2_PAGINATION_TOKENS) {
    return Array.from({ length: pageCount }, (_value, index) => index);
  }

  const lastPage = pageCount - 1;
  if (page <= 3) {
    return [0, 1, 2, 3, 4, 'ellipsis', lastPage];
  }

  if (page >= lastPage - 3) {
    return [0, 'ellipsis', lastPage - 4, lastPage - 3, lastPage - 2, lastPage - 1, lastPage];
  }

  return [0, 'ellipsis', page - 1, page, page + 1, 'ellipsis', lastPage];
};

export const formatTableV2DateTime = (value: Date | string | null | undefined): string => {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.valueOf())) return '-';
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const formatTableV2Date = (value: Date | string | null | undefined): string => {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.valueOf())) return '-';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const tableV2RelativeTimeSx = {
  '&.MuiTypography-root': {
    color: (theme) => (isLightTheme(theme) ? 'rgba(0, 0, 0, 0.68)' : theme.app.colors.textMuted),
    fontSize: '12px',
    opacity: (theme) => (isLightTheme(theme) ? 1 : 0.72),
  },
} satisfies SxProps<Theme>;

export const tableV2DateTimePrimarySx = {
  color: (theme) => (isLightTheme(theme) ? 'rgba(0, 0, 0, 0.68)' : theme.app.colors.textMuted),
  fontSize: '12px',
  fontVariantNumeric: 'tabular-nums',
} satisfies SxProps<Theme>;

export const TableV2ActionsHeaderIcon = (): ReactElement => (
  <Tooltip title="Actions">
    <Box
      component="span"
      role="img"
      sx={{
        alignItems: 'center',
        display: 'inline-flex',
        height: '100%',
        justifyContent: 'center',
        lineHeight: 0,
        width: '100%',
      }}
    >
      <SettingsIcon
        sx={{
          color: (theme) => theme.app.colors.textMuted,
          display: 'block',
          fontSize: (theme) => theme.app.iconSizes.sm,
          opacity: 0.72,
          strokeWidth: 0,
        }}
      />
    </Box>
  </Tooltip>
);

export const TableV2RowActionsIconButton = ({ label }: { label: string }): ReactElement => (
  <IconButton
    aria-label={label}
    size="medium"
    sx={{
      borderRadius: r('pill'),
      height: '28px',
      minHeight: '28px',
      minWidth: '28px',
      p: s('none'),
      width: '28px',
      '&:hover, &.Mui-focusVisible': {
        backgroundColor: tableControlHoverBackground,
        color: 'text.primary',
      },
      '&:focus-visible': {
        outline: (theme) => `2px solid ${theme.app.state.focus}`,
        outlineOffset: 2,
      },
    }}
  >
    <MoreVertIcon />
  </IconButton>
);

export const TableV2HeaderLabel = ({ label }: { label: string }): ReactElement => (
  <OverflowTooltip
    title={label}
    wrapperSx={{
      maxWidth: '100%',
      minWidth: 0,
      width: '100%',
    }}
  >
    <Box component="span" sx={tableV2HeaderLabelSx}>
      {label}
    </Box>
  </OverflowTooltip>
);

export const TableV2ToneChip = ({ label, tone }: { label: string; tone: UiTone }): ReactElement => {
  const resolvedDarkModeStatusColor = uiTokens.statusColors[tone];
  const resolvedLightModeStatusColor = lightModeStatusColors[tone];
  const resolvedLightModeBackground = alpha(resolvedLightModeStatusColor, 0.18);
  const resolvedLightModeFontWeight = typographyTokens.bodyMedium.fontWeight;
  const resolveStatusColor = (theme: Theme): string =>
    isLightTheme(theme) ? resolvedLightModeStatusColor : resolvedDarkModeStatusColor;

  return (
    <Chip
      size="small"
      tone={tone}
      label={label}
      sx={{
        backgroundColor: (theme) =>
          isLightTheme(theme) ? resolvedLightModeBackground : 'transparent',
        border: (theme) => `1px solid ${alpha(resolveStatusColor(theme), 0.82)}`,
        borderRadius: r('sm'),
        color: resolveStatusColor,
        fontWeight: resolvedLightModeFontWeight,
        height: '24px',
        '& .MuiChip-label': {
          color: 'inherit',
          px: s('md'),
          py: s('none'),
        },
        '& .MuiChip-label .MuiTypography-root': {
          color: resolveStatusColor,
          fontSize: '13px !important',
          fontWeight: resolvedLightModeFontWeight,
          lineHeight: 1.2,
        },
        '&:hover, &.Mui-focusVisible, &:active': {
          backgroundColor: (theme) =>
            isLightTheme(theme) ? resolvedLightModeBackground : 'transparent',
        },
      }}
    />
  );
};

export const TableV2StatusChip = ({
  label,
  status,
}: {
  label?: string;
  status: StatusChipStatus | null | undefined;
}): ReactElement => (
  <TableV2ToneChip
    label={label ?? resolveStatusChipLabel(status ?? 'untested')}
    tone={resolveStatusChipTone(status)}
  />
);

export const TableV2DateTimeCell = ({
  value,
}: {
  value: Date | string | null | undefined;
}): ReactElement => {
  const relativeLabel = value ? formatRelativeTime(value) : '';
  const dateTimeLabel = formatTableV2DateTime(value);
  const relativeTimeLabel = relativeLabel.length > 0 ? relativeLabel : '-';

  return (
    <Box sx={{ display: 'grid', gap: '1px', minWidth: 0 }}>
      <OverflowTooltip title={dateTimeLabel} wrapperSx={{ lineHeight: 1.25 }}>
        <Text
          role="body"
          component="span"
          sx={[
            tableV2DateTimePrimarySx,
            {
              display: 'block',
              lineHeight: 1.25,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          ]}
        >
          {dateTimeLabel}
        </Text>
      </OverflowTooltip>
      <OverflowTooltip title={relativeTimeLabel} wrapperSx={{ lineHeight: 1.2 }}>
        <Text
          role="captionSmall"
          component="span"
          sx={[
            tableV2RelativeTimeSx,
            {
              display: 'block',
              lineHeight: 1.2,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          ]}
        >
          {relativeTimeLabel}
        </Text>
      </OverflowTooltip>
    </Box>
  );
};

export const TableV2DateCell = ({
  value,
}: {
  value: Date | string | null | undefined;
}): ReactElement => {
  const dateLabel = formatTableV2Date(value);

  return (
    <OverflowTooltip title={dateLabel} wrapperSx={{ lineHeight: 1.25 }}>
      <Text
        role="body"
        component="span"
        sx={[
          tableV2DateTimePrimarySx,
          {
            display: 'block',
            lineHeight: 1.25,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        ]}
      >
        {dateLabel}
      </Text>
    </OverflowTooltip>
  );
};

export const TableV2TagGroup = ({
  tagColorByName,
  tags,
}: {
  tagColorByName?: Partial<Record<string, TagColor>>;
  tags: string[];
}): ReactElement => {
  const shouldLoadFallbackTagColours =
    tags.length > 0 && (!tagColorByName || Object.keys(tagColorByName).length === 0);
  const { tagOptions } = useTagOptions({ enabled: shouldLoadFallbackTagColours });
  const fallbackTagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const resolvedTagColorByName = useMemo(
    () => mergeTagColorByName(fallbackTagColorByName, tagColorByName),
    [fallbackTagColorByName, tagColorByName],
  );

  if (tags.length === 0) {
    return (
      <Text role="body" component="span" color="textSecondary">
        -
      </Text>
    );
  }

  const tagGroupSx = tags.length > 2 ? tableV2DenseTagGroupSx : tableV2TagGroupSx;
  const tagChipSx = tags.length > 2 ? [tableV2TagChipSx, tableV2DenseTagChipSx] : tableV2TagChipSx;
  const tagChipWrapperSx = tags.length > 2 ? tableV2DenseTagChipWrapperSx : undefined;

  return (
    <Box sx={tagGroupSx}>
      {tags.map((tag) => {
        const tagTone = resolveTagColor(tag, resolvedTagColorByName);

        return (
          <TableTagChipWithTooltip
            key={tag}
            label={tag}
            sx={tagChipSx}
            tone={tagTone}
            tooltipSx={tableV2TooltipTagChipSx}
            wrapperSx={tagChipWrapperSx}
          />
        );
      })}
    </Box>
  );
};

export const TableV2SortableHeader = <TField extends string>({
  activeSort,
  disabled = false,
  field,
  label,
  onSortChange,
}: {
  activeSort: ApiSort;
  disabled?: boolean;
  field: TField;
  label: string;
  onSortChange: (field: TField) => void;
}): ReactElement => {
  const active = activeSort.field === field;
  const direction = active ? activeSort.direction : 'desc';

  if (disabled) {
    return <TableV2HeaderLabel label={label} />;
  }

  return (
    <Box
      aria-label={
        active
          ? `${label}, sorted ${direction === 'asc' ? 'ascending' : 'descending'}`
          : `${label}, activate to sort descending`
      }
      component="button"
      type="button"
      onClick={() => {
        onSortChange(field);
      }}
      sx={{
        '&:hover .TableV2SortableHeader-icon': {
          opacity: 1,
        },
        '&:focus-visible': {
          outline: (theme) => `2px solid ${theme.app.state.focus}`,
          outlineOffset: 2,
        },
        alignItems: 'center',
        backgroundColor: 'transparent',
        border: 0,
        color: 'inherit',
        cursor: 'pointer',
        display: 'inline-flex',
        font: 'inherit',
        gap: s('xs'),
        justifyContent: 'flex-start',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
        p: s('none'),
        textAlign: 'left',
      }}
    >
      <OverflowTooltip
        title={label}
        wrapperSx={{
          flex: '0 1 auto',
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        <Box component="span" sx={tableV2HeaderLabelSx}>
          {label}
        </Box>
      </OverflowTooltip>
      <KeyboardArrowDownIcon
        className="TableV2SortableHeader-icon"
        sx={{
          flexShrink: 0,
          fontSize: iconSizes.md,
          opacity: active ? 1 : 0,
          transform: direction === 'asc' ? 'rotate(180deg)' : 'rotate(0deg)',
        }}
      />
    </Box>
  );
};
